import { useState, useEffect, useRef, useCallback } from 'react';
const useScoringSystem = (initialState, onScoreUpdate, playSound) => {
  const [score, setScore] = useState(initialState.score || 0);
  const [multiplier, setMultiplier] = useState(initialState.multiplier || 1);
  const [accumulatorProgress, setAccumulatorProgress] = useState(initialState.accumulatorProgress || 0);
  
  const accumulatorAnimationRef = useRef(null);
  const lastUpdateTimestampRef = useRef(null);
  const multiplierDecreaseTriggeredRef = useRef(false);
  const accumulatorProgressRef = useRef(initialState.accumulatorProgress || 0);
  const multiplierRef = useRef(initialState.multiplier || 1);
  const animationActiveRef = useRef(false);

  useEffect(() => {
    accumulatorProgressRef.current = accumulatorProgress;
  }, [accumulatorProgress]);

  useEffect(() => {
    multiplierRef.current = multiplier;
  }, [multiplier]);

  useEffect(() => {
    onScoreUpdate({
      score,
      multiplier,
      accumulatorProgress
    });
  }, [score, multiplier, accumulatorProgress, onScoreUpdate]);

  const increaseScore = useCallback(() => {
    const newScore = score + 10 * multiplierRef.current;
    setScore(newScore);
  }, [score]);

  const startAccumulatorTimer = useCallback(() => {
    lastUpdateTimestampRef.current = performance.now();
    animationActiveRef.current = true;

    if (accumulatorAnimationRef.current) {
      cancelAnimationFrame(accumulatorAnimationRef.current);
    }

    accumulatorAnimationRef.current = requestAnimationFrame(updateAccumulator);
  }, []);

  const stopAccumulatorTimer = useCallback(() => {
    animationActiveRef.current = false;

    if (accumulatorAnimationRef.current) {
      cancelAnimationFrame(accumulatorAnimationRef.current);
      accumulatorAnimationRef.current = null;
    }
  }, []);

  const updateAccumulator = useCallback((timestamp) => {
    if (!animationActiveRef.current) {
      return;
    }

    if (!lastUpdateTimestampRef.current) {
      lastUpdateTimestampRef.current = timestamp;
      accumulatorAnimationRef.current = requestAnimationFrame(updateAccumulator);
      return;
    }

    const deltaTime = timestamp - lastUpdateTimestampRef.current;
    const reductionAmount = (0.016 / 1000) * deltaTime;
    
    let newAccumulatorProgress = Math.max(0, accumulatorProgressRef.current - reductionAmount);
    let shouldFlashMultiplier = false;

    if (newAccumulatorProgress === 0 && accumulatorProgressRef.current > 0) {
      if (!multiplierDecreaseTriggeredRef.current && multiplierRef.current > 1) {
        multiplierDecreaseTriggeredRef.current = true;
        shouldFlashMultiplier = true;

        const newMultiplier = multiplierRef.current - 1;
        setMultiplier(newMultiplier);
        multiplierRef.current = newMultiplier;

        newAccumulatorProgress = 1;
        playSound('multiplyDown');
      }
    } else if (newAccumulatorProgress > 0) {
      multiplierDecreaseTriggeredRef.current = false;
    }

    if (Math.abs(accumulatorProgressRef.current - newAccumulatorProgress) > 0.001) {
      setAccumulatorProgress(newAccumulatorProgress);
      accumulatorProgressRef.current = newAccumulatorProgress;
    }

    if (animationActiveRef.current) {
      lastUpdateTimestampRef.current = timestamp;
      accumulatorAnimationRef.current = requestAnimationFrame(updateAccumulator);
    }

    return shouldFlashMultiplier;
  }, [playSound]);

  const reduceAccumulator = useCallback(() => {
    const currentProgress = accumulatorProgressRef.current;
    if (currentProgress <= 0.05) return;

    const newProgress = Math.max(currentProgress - 0.1, 0);
    setAccumulatorProgress(newProgress);
    accumulatorProgressRef.current = newProgress;
  }, []);

  const increaseAccumulator = useCallback((boost) => {
    const currentProgress = accumulatorProgressRef.current;
    let newProgress = currentProgress + boost;

    if (newProgress >= 1) {
      const overflow = newProgress - 1;
      const newMultiplier = multiplierRef.current + 1;
      
      setMultiplier(newMultiplier);
      multiplierRef.current = newMultiplier;
      playSound('multiplyUp');
      
      newProgress = overflow;
    }

    setAccumulatorProgress(newProgress);
    accumulatorProgressRef.current = newProgress;
  }, [playSound]);

  useEffect(() => {
    if (accumulatorProgress > 0) {
      const reductionInterval = setInterval(() => {
        const currentAccumulatorProgress = accumulatorProgressRef.current;
        const currentMultiplier = multiplierRef.current;

        if (currentAccumulatorProgress > 0) {
          const reductionAmount = 0.016;
          let newAccumulatorProgress = Math.max(
            0,
            currentAccumulatorProgress - reductionAmount
          );

          if (newAccumulatorProgress === 0 && currentAccumulatorProgress > 0) {
            if (currentMultiplier > 1) {
              const newMultiplier = currentMultiplier - 1;
              setMultiplier(newMultiplier);
              multiplierRef.current = newMultiplier;

              newAccumulatorProgress = 1;

              playSound('multiplyDown');
            }
          }

          setAccumulatorProgress(newAccumulatorProgress);
          accumulatorProgressRef.current = newAccumulatorProgress;
        }
      }, 1000);

      return () => {
        clearInterval(reductionInterval);
      };
    }
  }, [accumulatorProgress]);

  useEffect(() => {
    return () => {
      if (accumulatorAnimationRef.current) {
        cancelAnimationFrame(accumulatorAnimationRef.current);
        accumulatorAnimationRef.current = null;
      }
    };
  }, []);

  return {
    score,
    setScore,
    multiplier,
    setMultiplier,
    accumulatorProgress,
    setAccumulatorProgress,
    accumulatorProgressRef,
    multiplierRef,
    animationActiveRef,
    increaseScore,
    startAccumulatorTimer,
    stopAccumulatorTimer,
    reduceAccumulator,
    increaseAccumulator
  };
};

export default useScoringSystem;
